import { CsvEnrichment } from "../../../CsvEnrichment/schema/csvEnrichment";
import { ScrapeRequest, ScrapeRequestStatus } from "../../../CustomEvent/schema/scrapeRequest";

/** How a delivery is presented in the scraped-files table. */
export enum ScrapedFileStatus {
	/** The newest delivered file — the one the list enriches from next. */
	LATEST = "LATEST",
	/** An older delivered file; its leads stay in the list, it is simply no longer the newest. */
	SUPERSEDED = "SUPERSEDED",
	/** Ordered, not delivered yet. */
	PROCESSING = "PROCESSING",
}

export interface ScrapedFileRow {
	key: string;
	status: ScrapedFileStatus;
	file?: CsvEnrichment;
	request?: ScrapeRequest;
}

const deliveredAtOf = (request: ScrapeRequest): number =>
	request.deliveredAt ? new Date(request.deliveredAt).getTime() : 0;

/**
 * Builds the rows of the scraped-files table from a source's deliveries and the files attached to
 * the list (PREC-3091).
 *
 * A delivery is matched to its file by `csvEnrichmentId`; the newest delivered one is "Latest" and
 * every older one "Superseded". Files with no delivery row still show up — sources that predate the
 * scrape-request table, or a file attached out of band — so nothing the list holds is hidden.
 */
export const buildScrapedFileRows = (
	requests: ScrapeRequest[],
	csvEnrichments: CsvEnrichment[]
): ScrapedFileRow[] => {
	const fileById = new Map(csvEnrichments.map((file) => [file.id, file]));

	const delivered = requests
		.filter((request) => request.status === ScrapeRequestStatus.DELIVERED)
		.sort((a, b) => deliveredAtOf(b) - deliveredAtOf(a));

	const open = requests.filter((request) => request.status === ScrapeRequestStatus.REQUESTED);

	const rows: ScrapedFileRow[] = open.map((request) => ({
		key: `request-${request.id}`,
		status: ScrapedFileStatus.PROCESSING,
		request,
	}));

	const matchedFileIds = new Set<number>();

	delivered.forEach((request, index) => {
		const file = request.csvEnrichmentId ? fileById.get(request.csvEnrichmentId) : undefined;

		if (file) {
			matchedFileIds.add(file.id);
		}

		rows.push({
			key: `request-${request.id}`,
			status: index === 0 ? ScrapedFileStatus.LATEST : ScrapedFileStatus.SUPERSEDED,
			file,
			request,
		});
	});

	// Files the list holds that no delivery row claims — keep them visible, newest first.
	const orphans = csvEnrichments
		.filter((file) => !matchedFileIds.has(file.id))
		.sort((a, b) => b.id - a.id);

	const hasDelivered = delivered.length > 0;

	orphans.forEach((file, index) => {
		rows.push({
			key: `file-${file.id}`,
			status:
				!hasDelivered && index === 0 ? ScrapedFileStatus.LATEST : ScrapedFileStatus.SUPERSEDED,
			file,
		});
	});

	return rows;
};

/** Whether a re-scrape can be ordered — never while a previous request is still open. */
export const hasOpenScrapeRequest = (requests: ScrapeRequest[]): boolean =>
	requests.some((request) => request.status === ScrapeRequestStatus.REQUESTED);

/** Duplicate rows detected in the newest file, once its mapping has been saved. */
export const latestDuplicateCount = (rows: ScrapedFileRow[]): number | null => {
	const latest = rows.find((row) => row.status === ScrapedFileStatus.LATEST);

	return latest?.file?.duplicateCount ?? null;
};
